"use client";

import { toast as sonner } from "sonner";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";

/* ─────────────────────────────────────────────────────────────
   SNA toast layer
   · drop-in replacement for `import { toast } from "sonner"`
   · every message (server or client) is translated to the
     active locale through the dictionary below
   · custom luxury card (navy / gold), RTL-aware
   ───────────────────────────────────────────────────────────── */

type Kind = "success" | "error" | "warning" | "info";
type Pair = { ar: string; en: string };

/* key = message as emitted by the server / client (any language) */
const DICT: Record<string, Pair> = {
  /* cart */
  "Property already in cart": { ar: "هذا العقار موجود في السلة بالفعل", en: "This property is already in your cart" },
  "This product is already in his shopping cart": { ar: "هذا العقار موجود في السلة بالفعل", en: "This property is already in your cart" },
  "Property added to cart successfully": { ar: "تمت إضافة العقار إلى السلة", en: "Property added to your cart" },
  "product added successfully": { ar: "تمت إضافة العقار إلى السلة", en: "Property added to your cart" },
  "Warning adding property to cart": { ar: "تعذّر إضافة العقار إلى السلة", en: "Couldn't add the property to your cart" },
  "Error adding property to cart": { ar: "حدث خطأ أثناء الإضافة إلى السلة", en: "Error adding the property to your cart" },
  "Item removed from cart successfully": { ar: "تمت إزالة العقار من السلة", en: "Property removed from your cart" },
  "Cart cleared successfully": { ar: "تم تفريغ السلة", en: "Cart cleared" },
  "Cart emptied and removed": { ar: "تم تفريغ السلة", en: "Cart cleared" },
  "Error clearing cart": { ar: "حدث خطأ أثناء تفريغ السلة", en: "Error clearing the cart" },
  "Item not found in cart": { ar: "العقار غير موجود في السلة", en: "Item not found in your cart" },
  "Cart not found": { ar: "السلة فارغة", en: "Your cart is empty" },
  "One or more properties are already sold": { ar: "أحد العقارات في سلتك تم بيعه بالفعل", en: "One or more properties in your cart were already sold" },
  "You can only compare up to 3 properties": { ar: "يمكنك مقارنة 3 عقارات كحد أقصى", en: "You can compare up to 3 properties only" },

  /* favorites */
  "تمت الإضافة للمفضلة": { ar: "تمت الإضافة إلى المفضلة", en: "Added to favorites" },
  "تمت الإزالة من المفضلة": { ar: "تمت الإزالة من المفضلة", en: "Removed from favorites" },

  /* auth */
  "Login successful": { ar: "تم تسجيل الدخول بنجاح", en: "Signed in successfully" },
  "Logout successful": { ar: "تم تسجيل الخروج", en: "Signed out" },
  "User logged out successfully!": { ar: "تم تسجيل الخروج", en: "Signed out" },
  "Failed to log out user.": { ar: "تعذّر تسجيل الخروج", en: "Couldn't sign you out" },
  "Invalid credentials": { ar: "البريد الإلكتروني أو كلمة المرور غير صحيحة", en: "Incorrect email or password" },
  "Account not active or email not verified": { ar: "الحساب غير مفعّل أو البريد غير موثّق", en: "Account inactive or email not verified" },
  "please verify your email address to activate your account": { ar: "يرجى توثيق بريدك الإلكتروني لتفعيل الحساب", en: "Please verify your email to activate your account" },
  "Check your email to verify your account": { ar: "تحقق من بريدك الإلكتروني لتفعيل الحساب", en: "Check your email to verify your account" },
  "Check your email to verify password": { ar: "تحقق من بريدك الإلكتروني لإعادة تعيين كلمة المرور", en: "Check your email to reset your password" },
  "check your email for reset your password": { ar: "تحقق من بريدك الإلكتروني لإعادة تعيين كلمة المرور", en: "Check your email to reset your password" },
  "Email verified successfully": { ar: "تم توثيق البريد الإلكتروني بنجاح", en: "Email verified successfully" },
  "Email verified successfully!": { ar: "تم توثيق البريد الإلكتروني بنجاح", en: "Email verified successfully" },
  "Failed to verify email.": { ar: "تعذّر توثيق البريد الإلكتروني", en: "Couldn't verify your email" },
  "Invalid verification code": { ar: "رمز التحقق غير صحيح", en: "Invalid verification code" },
  "Verification code expired": { ar: "انتهت صلاحية رمز التحقق", en: "Verification code expired" },
  "Verification email resent successfully": { ar: "تم إعادة إرسال رسالة التفعيل", en: "Verification email resent" },
  "Verification email resent successfully!": { ar: "تم إعادة إرسال رسالة التفعيل", en: "Verification email resent" },
  "Password reset successfully!": { ar: "تم إعادة تعيين كلمة المرور بنجاح", en: "Password reset successfully" },
  "Failed to reset password.": { ar: "تعذّر إعادة تعيين كلمة المرور", en: "Couldn't reset your password" },
  "Password updated successfully": { ar: "تم تحديث كلمة المرور", en: "Password updated" },
  "User password updated successfully!": { ar: "تم تحديث كلمة المرور", en: "Password updated" },
  "Current password is incorrect": { ar: "كلمة المرور الحالية غير صحيحة", en: "Current password is incorrect" },
  "Unauthorized": { ar: "يرجى تسجيل الدخول أولاً", en: "Please sign in first" },
  "غير مصرح بالدخول": { ar: "يرجى تسجيل الدخول أولاً", en: "Please sign in first" },
  "Access denied": { ar: "غير مصرح لك بهذا الإجراء", en: "You're not allowed to do this" },
  "Forbidden": { ar: "غير مصرح لك بهذا الإجراء", en: "You're not allowed to do this" },
  "User not found": { ar: "المستخدم غير موجود", en: "User not found" },

  /* profile */
  "User profile updated successfully": { ar: "تم تحديث بيانات الحساب", en: "Profile updated" },
  "User profile updated successfully!": { ar: "تم تحديث بيانات الحساب", en: "Profile updated" },
  "User photo updated successfully": { ar: "تم تحديث الصورة الشخصية", en: "Profile photo updated" },
  "User photo updated successfully!": { ar: "تم تحديث الصورة الشخصية", en: "Profile photo updated" },
  "Media updated successfully": { ar: "تم تحديث الصورة الشخصية", en: "Profile photo updated" },

  /* identity */
  "تم حفظ بيانات الهوية بشكل مشفّر وآمن": { ar: "تم حفظ بيانات الهوية بشكل مشفّر وآمن", en: "Identity saved securely (encrypted)" },
  "Identity saved securely": { ar: "تم حفظ بيانات الهوية بشكل مشفّر وآمن", en: "Identity saved securely (encrypted)" },
  "Could not save identity": { ar: "تعذّر حفظ بيانات الهوية", en: "Couldn't save your identity details" },
  "يرجى إدخال رقم الهوية / الإقامة قبل إتمام الدفع": { ar: "يرجى إدخال رقم الهوية / الإقامة قبل إتمام الدفع", en: "Please add your ID / Iqama number before paying" },
  "يجب أن يكون عمرك 18 سنة أو أكثر لإتمام عملية شراء/إيجار": { ar: "يجب أن يكون عمرك 18 سنة أو أكثر لإتمام عملية شراء/إيجار", en: "You must be 18 or older to buy or rent" },
  "تاريخ الميلاد غير صالح": { ar: "تاريخ الميلاد غير صالح", en: "Invalid date of birth" },
  "يجب أن يتكون الرقم من 10 أرقام ويبدأ بـ 1 (هوية وطنية) أو 2 (إقامة)": { ar: "يجب أن يتكون الرقم من 10 أرقام ويبدأ بـ 1 (هوية وطنية) أو 2 (إقامة)", en: "The number must be 10 digits starting with 1 (National ID) or 2 (Iqama)" },
  "رقم الهوية غير صحيح — تأكد من الأرقام": { ar: "رقم الهوية غير صحيح — تأكد من الأرقام", en: "Invalid ID number — please re-check the digits" },

  /* payment / orders */
  "Payment processed successfully": { ar: "جارٍ تحويلك إلى بوابة الدفع الآمنة", en: "Redirecting you to the secure payment gateway" },
  "Error processing payment": { ar: "حدث خطأ أثناء معالجة الدفع", en: "Error processing the payment" },
  "Refund successful": { ar: "تم استرداد المبلغ بنجاح", en: "Refund completed" },
  "Order not found": { ar: "الطلب غير موجود", en: "Order not found" },
  "الطلب غير موجود": { ar: "الطلب غير موجود", en: "Order not found" },
  "No orders found": { ar: "لا توجد طلبات", en: "No orders found" },
  "Invalid order": { ar: "الطلب غير صالح", en: "Invalid order" },

  /* services & quotes */
  "تم إرسال طلبك، سيتواصل معك فريقنا قريباً.": { ar: "تم إرسال طلبك، سيتواصل معك فريقنا قريباً", en: "Request sent — our team will contact you shortly" },
  "تم التحديث": { ar: "تم التحديث", en: "Updated" },
  "تمت الموافقة": { ar: "تمت الموافقة على عرض السعر", en: "Quote accepted" },
  "Accepted": { ar: "تمت الموافقة على عرض السعر", en: "Quote accepted" },
  "لا يوجد عرض سعر للرد عليه": { ar: "لا يوجد عرض سعر للرد عليه", en: "There's no quote to respond to" },
  "نوع الخدمة غير صالح": { ar: "نوع الخدمة غير صالح", en: "Invalid service type" },
  "يرجى تقديم سبب": { ar: "يرجى كتابة السبب", en: "Please provide a reason" },

  /* contact / complaints / reviews */
  "Message received": { ar: "تم استلام رسالتك، سنرد عليك قريباً", en: "Message received — we'll get back to you soon" },
  "Message sent successfully": { ar: "تم إرسال رسالتك بنجاح", en: "Message sent successfully" },
  "Failed to send message": { ar: "تعذّر إرسال الرسالة", en: "Couldn't send your message" },
  "الرجاء تقديم الاسم الكامل، البريد الإلكتروني، ورقم الهاتف": { ar: "يرجى إدخال الاسم الكامل والبريد الإلكتروني ورقم الهاتف", en: "Please provide your full name, email and phone number" },
  "تم تسجيل شكواك بنجاح. سنقوم بالرد عليك في أقرب وقت ممكن.": { ar: "تم تسجيل شكواك، سنرد عليك في أقرب وقت", en: "Complaint submitted — we'll respond as soon as possible" },
  "تم إرسال تقييمك بنجاح. شكراً لك!": { ar: "تم إرسال تقييمك، شكراً لك", en: "Thanks! Your review was submitted" },
  "لقد قمت بتقييم هذا البائع من قبل ولا يمكن التعديل.": { ar: "لقد قيّمت هذا البائع من قبل", en: "You've already reviewed this seller" },
  "لا يمكنك تقييم هذا البائع — لا يوجد لديك عملية شراء معه.": { ar: "لا يمكنك تقييم بائع لم تتعامل معه", en: "You can only review sellers you've bought from" },
  "التقييم يجب أن يكون من 1 إلى 5": { ar: "التقييم يجب أن يكون من 1 إلى 5", en: "Rating must be between 1 and 5" },

  /* financing */
  "Application submitted successfully": { ar: "تم إرسال طلب التمويل بنجاح", en: "Financing application submitted" },
  "Application cancelled successfully": { ar: "تم إلغاء طلب التمويل", en: "Financing application cancelled" },
  "تم تقديم بيانات التمويل بنجاح": { ar: "تم إرسال طلب التمويل بنجاح", en: "Financing application submitted" },
  "تم إلغاء طلب التمويل بنجاح": { ar: "تم إلغاء طلب التمويل", en: "Financing application cancelled" },
  "لديك بالفعل طلب تمويل قيد المعالجة لهذا البرنامج": { ar: "لديك طلب تمويل قيد المعالجة لهذا البرنامج بالفعل", en: "You already have a pending application for this program" },
  "راتبك لا يتوافق مع أي شريحة تمويلية في هذا البرنامج": { ar: "راتبك لا يتوافق مع شرائح هذا البرنامج", en: "Your salary doesn't match any tier of this program" },
  "برنامج التمويل غير موجود": { ar: "برنامج التمويل غير موجود", en: "Financing program not found" },
  "طلب التمويل غير موجود": { ar: "طلب التمويل غير موجود", en: "Financing application not found" },

  /* properties */
  "Property not found": { ar: "العقار غير موجود", en: "Property not found" },
  "property not found": { ar: "العقار غير موجود", en: "Property not found" },
  "No properties found": { ar: "لا توجد عقارات مطابقة", en: "No matching properties" },
  "يجب دفع 40 ريال لإضافة عقار جديد": { ar: "يجب دفع 40 ريال لإضافة عقار جديد", en: "A 40 SAR fee is required to add a new property" },

  /* generic */
  "Error": { ar: "حدث خطأ، حاول مرة أخرى", en: "Something went wrong, please try again" },
  "Server Error": { ar: "خطأ في الخادم، حاول لاحقاً", en: "Server error, please try again later" },
  "Server error": { ar: "خطأ في الخادم، حاول لاحقاً", en: "Server error, please try again later" },
  "Internal server error": { ar: "خطأ في الخادم، حاول لاحقاً", en: "Server error, please try again later" },
  "something wrong try again later": { ar: "حدث خطأ، حاول مرة أخرى لاحقاً", en: "Something went wrong, please try again later" },
  "البيانات غير كاملة": { ar: "يرجى إكمال جميع الحقول", en: "Please complete all fields" },
  "يرجى إكمال جميع الحقول المطلوبة": { ar: "يرجى إكمال جميع الحقول المطلوبة", en: "Please complete all required fields" },
  "Network Error": { ar: "تحقق من اتصالك بالإنترنت", en: "Check your internet connection" },
};

/* punctuation-insensitive lookup */
const norm = (s: string) => s.trim().replace(/[.!؟?]+$/g, "").toLowerCase();
const INDEX = new Map<string, Pair>(Object.entries(DICT).map(([k, v]) => [norm(k), v]));

const getLocale = (): "ar" | "en" => {
  if (typeof document === "undefined") return "ar";
  const lang = document.documentElement.lang;
  if (lang === "ar" || lang === "en") return lang;
  return window.location.pathname.startsWith("/en") ? "en" : "ar";
};

export function translateMessage(raw: unknown, locale = getLocale()): string {
  const s = typeof raw === "string" ? raw : String(raw ?? "");
  const hit = INDEX.get(norm(s));
  if (hit) return hit[locale];
  /* mongoose-style validation errors → generic */
  if (/validation failed|cast to objectid|E11000/i.test(s)) {
    return locale === "ar" ? "البيانات المدخلة غير صالحة" : "The submitted data is invalid";
  }
  return s;
}

/* ── card ─────────────────────────────────────────────────── */
const THEME: Record<Kind, { icon: typeof CheckCircle2; accent: string; glow: string; label: Pair }> = {
  success: { icon: CheckCircle2, accent: "#2fb67c", glow: "rgba(47,182,124,0.35)", label: { ar: "تم بنجاح", en: "Success" } },
  error: { icon: XCircle, accent: "#e5484d", glow: "rgba(229,72,77,0.35)", label: { ar: "تنبيه", en: "Error" } },
  warning: { icon: AlertTriangle, accent: "#d9b25a", glow: "rgba(217,178,90,0.4)", label: { ar: "انتبه", en: "Heads up" } },
  info: { icon: Info, accent: "#5b8def", glow: "rgba(91,141,239,0.35)", label: { ar: "معلومة", en: "Info" } },
};

function Card({ id, kind, message, title }: { id: string | number; kind: Kind; message: string; title?: string }) {
  const locale = getLocale();
  const ar = locale === "ar";
  const t = THEME[kind];
  const Icon = t.icon;
  return (
    <div
      dir={ar ? "rtl" : "ltr"}
      role="status"
      className="sna-toast pointer-events-auto relative flex w-[min(92vw,400px)] items-center gap-3.5 overflow-hidden rounded-2xl px-4 py-3.5 text-white"
      style={{
        background: "linear-gradient(135deg,#0f2f7a 0%,#0a1e4f 55%,#071535 100%)",
        boxShadow: `0 24px 60px -20px rgba(7,21,53,0.75), 0 0 0 1px rgba(255,255,255,0.06) inset, 0 0 40px -14px ${t.glow}`,
      }}
    >
      {/* gold hairline + accent bar */}
      <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#d9b25a]/80 to-transparent" />
      <span className={`absolute inset-y-2 ${ar ? "right-0" : "left-0"} w-1 rounded-full`} style={{ background: t.accent }} />
      <span className="pointer-events-none absolute -top-10 -end-6 size-28 rounded-full blur-2xl" style={{ background: t.glow }} />

      <span
        className="relative grid size-10 shrink-0 place-items-center rounded-xl"
        style={{ background: `${t.accent}22`, color: t.accent, boxShadow: `0 0 0 1px ${t.accent}55 inset` }}
      >
        <Icon size={20} strokeWidth={2.4} />
      </span>

      <div className="relative min-w-0 flex-1">
        <p className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: t.accent }}>
          {title || t.label[locale]}
        </p>
        <p className="mt-0.5 text-sm font-bold leading-snug text-white/95">{message}</p>
      </div>

      <button
        onClick={() => sonner.dismiss(id)}
        aria-label="close"
        className="relative grid size-7 shrink-0 place-items-center rounded-full text-white/50 transition hover:bg-white/10 hover:text-white"
      >
        <X size={14} />
      </button>

      {/* progress hairline */}
      <span className="sna-toast-bar pointer-events-none absolute bottom-0 h-[2px]" style={{ background: t.accent, insetInlineStart: 0 }} />
    </div>
  );
}

type Opts = { title?: string; duration?: number; id?: string | number };
const DUR: Record<Kind, number> = { success: 3500, info: 4000, warning: 4500, error: 5000 };

function fire(kind: Kind, raw: unknown, opts: Opts = {}) {
  const message = translateMessage(raw);
  if (!message) return;
  const duration = opts.duration ?? DUR[kind];
  /* one toast per message at a time — no stacking duplicates */
  const id = opts.id ?? `${kind}:${message}`;
  return sonner.custom((tid) => <Card id={tid} kind={kind} message={message} title={opts.title} />, {
    id,
    duration,
    unstyled: true,
    style: { ["--sna-dur" as string]: `${duration}ms` },
  });
}

/* drop-in replacement for sonner's `toast` */
export const toast = Object.assign((raw: unknown, opts?: Opts) => fire("info", raw, opts), {
  success: (raw: unknown, opts?: Opts) => fire("success", raw, opts),
  error: (raw: unknown, opts?: Opts) => fire("error", raw, opts),
  warning: (raw: unknown, opts?: Opts) => fire("warning", raw, opts),
  info: (raw: unknown, opts?: Opts) => fire("info", raw, opts),
  dismiss: (id?: string | number) => sonner.dismiss(id),
  /* pull the right text from an axios error and show it */
  fromError: (err: any, fallback = "Error", opts?: Opts) =>
    fire("error", err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback, opts),
});

export default toast;
